import { nanoid } from 'nanoid';

/**
 * 历史记录去重 / 追加的共享工具。
 *
 * image / video 的 realtime 与 polling 完成回调都需要：
 * 1) 按输出 URL 判断该结果是否已在历史中（避免 realtime + polling 同时命中产生重复条目）；
 * 2) 命中则复用旧条目，否则追加由调用方构造的新条目。
 *
 * 这里只负责“去重 + 追加”，`generated` 的拼装仍由各调用方自行处理（image/video 字段不同）。
 */

type HistoryEntry = {
  id: string;
  url?: string;
  urls?: string[];
  [k: string]: unknown;
};

const keyOf = (urls: string[]): string => urls.join('::');

/** 按输出 URL 在历史中查找已存在的条目（urls 为空时视为无匹配）。 */
export function findHistoryByUrls(
  history: HistoryEntry[] | undefined,
  urls: string[],
): HistoryEntry | undefined {
  if (!Array.isArray(history) || urls.length === 0) return undefined;
  const key = keyOf(urls);
  return history.find((h) =>
    Array.isArray(h?.urls) && h.urls.length
      ? keyOf(h.urls) === key
      : Boolean(h?.url && h.url === urls[0]),
  );
}

/**
 * 命中已有历史则复用其 id（不改动数组引用），否则用 `buildEntry` 构造新条目并追加。
 * @returns 下一份历史数组与应当激活的历史 id。
 */
export function reuseOrAppendHistory(
  prevHistory: HistoryEntry[] | undefined,
  urls: string[],
  buildEntry: (id: string) => HistoryEntry,
): { history: HistoryEntry[]; activeHistoryId: string } {
  const base = Array.isArray(prevHistory) ? prevHistory : [];
  const existing = findHistoryByUrls(base, urls);
  if (existing) return { history: base, activeHistoryId: existing.id };
  const id = nanoid();
  return { history: [...base, buildEntry(id)], activeHistoryId: id };
}

/**
 * 将真实尺寸/时长等输出元数据回写进指定 id 的历史条目（与该条目的 url 绑定）。
 *
 * 设计原则：真实尺寸跟着每个历史条目一起存，切换历史时即可直接读取、无需重测。
 * 仅当条目存在、url 匹配（防止把当前测量误写到另一条目）、且 patch 确实带来变更时才返回新数组；
 * 否则原样返回，避免无意义的节点数据写入与反复保存。
 *
 * @param matchUrl 触发本次回写的来源 url；用于校验它确实是该历史条目的图/视频。
 */
export function patchHistoryItemById(
  history: HistoryEntry[] | undefined,
  id: string | undefined,
  matchUrl: string | undefined,
  patch: Record<string, unknown>,
): HistoryEntry[] | undefined {
  if (!Array.isArray(history) || !id) return history;
  const idx = history.findIndex((h) => h?.id === id);
  if (idx < 0) return history;
  const item = history[idx];
  if (matchUrl) {
    const itemUrl =
      Array.isArray(item?.urls) && item.urls.length ? item.urls[0] : item?.url;
    if (itemUrl && itemUrl !== matchUrl) return history;
  }
  const changed = Object.keys(patch).some((k) => (item as any)?.[k] !== patch[k]);
  if (!changed) return history;
  const next = history.slice();
  next[idx] = { ...item, ...patch };
  return next;
}
